import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  BookOpen, Loader2, ChevronRight, ChevronLeft, Maximize2, Minimize2,
  CheckCircle2, Circle, Trophy, Sparkles, ShoppingCart, ExternalLink,
  ZoomIn, ZoomOut
} from 'lucide-react';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chapter definitions - PDFs served through backend proxy
const chapters = [
  { id: "intro", title: "Introduction" },
  { id: "ch1", title: "Chapter 1: Stop Letting Busy Win" },
  { id: "ch2", title: "Chapter 2: When Life Knocks You Sideways" },
  { id: "ch3", title: "Chapter 3: Build Your Trust Muscles" },
  { id: "ch4", title: "Chapter 4: Let Go Of The Formula" },
  { id: "ch5", title: "Chapter 5: Honor Your Anger" },
  { id: "ch6", title: "Chapter 6: Untangle Your Identity" },
  { id: "ch7", title: "Chapter 7: Reclaim Your Process" },
  { id: "ch8", title: "Chapter 8: Redefine Your Relationship with Risk" },
  { id: "ch9", title: "Chapter 9: Choose Yourself, Again and Again" },
  { id: "ch10", title: "Chapter 10: Rewrite the Rules" },
  { id: "ch11", title: "Chapter 11: Reclaim the Voice in Your Head" },
  { id: "conclusion", title: "Conclusion: Coming Home to Your Own Light" },
];

const EbookPage = () => {
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [completedChapters, setCompletedChapters] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuth();
  
  // PDF viewer state
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    loadProgress();
  }, []);

  // Reset PDF state when chapter changes
  useEffect(() => {
    if (selectedChapter) {
      setNumPages(null);
      setCurrentPage(1);
      setPdfLoading(true);
      setPdfError(null);
    }
  }, [selectedChapter?.id]);

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/ebook/progress`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setCompletedChapters(response.data.completed_chapters || []);
      }
    } catch (error) {
      console.error('Failed to load progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentChapter = async (chapterId) => {
    try {
      await axios.post(`${API_URL}/ebook/progress/current`, 
        { chapter_id: chapterId },
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Failed to save current chapter:', error);
    }
  };

  const toggleChapterComplete = async (chapterId) => {
    const isCompleted = completedChapters.includes(chapterId);
    
    try {
      const endpoint = isCompleted 
        ? `${API_URL}/ebook/progress/uncomplete/${chapterId}`
        : `${API_URL}/ebook/progress/complete/${chapterId}`;
      
      const response = await axios.post(endpoint, {}, { headers: getAuthHeader() });
      setCompletedChapters(response.data.completed_chapters || []);
      
      if (!isCompleted) {
        toast.success('Chapter marked as complete!');
        
        if (response.data.completed_chapters?.length === chapters.length) {
          toast.success('Congratulations! You\'ve completed the entire ebook!', {
            duration: 5000
          });
        }
      }
    } catch (error) {
      console.error('Failed to update progress:', error);
      toast.error('Failed to update progress');
    }
  };

  const selectChapter = (chapter) => {
    setSelectedChapter(chapter);
    saveCurrentChapter(chapter.id);
  };

  const goToNextChapter = () => {
    const currentIndex = chapters.findIndex(ch => ch.id === selectedChapter?.id);
    if (currentIndex < chapters.length - 1) {
      const nextChapter = chapters[currentIndex + 1];
      selectChapter(nextChapter);
    }
  };

  const goToPreviousChapter = () => {
    const currentIndex = chapters.findIndex(ch => ch.id === selectedChapter?.id);
    if (currentIndex > 0) {
      const prevChapter = chapters[currentIndex - 1];
      selectChapter(prevChapter);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfLoading(false);
    setPdfError('Failed to load this chapter. Please try again.');
  };

  // Get PDF URL through backend proxy
  const getPdfUrl = (chapterId) => {
    return `${API_URL}/ebook/proxy/${chapterId}`;
  };

  const completionPercentage = Math.round((completedChapters.length / chapters.length) * 100);
  const currentIndex = chapters.findIndex(ch => ch.id === selectedChapter?.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Welcome screen if no chapter selected
  if (!selectedChapter) {
    return (
      <div className="space-y-6" data-testid="ebook-page">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-stone-900 mb-2">Illuminate Your Worth</h1>
            <p className="text-stone-600">by Heather Stewart</p>
          </div>
          
          {/* Progress Card */}
          <div className="card-cosmic p-4 flex items-center gap-4 min-w-[280px]">
            <div className="relative">
              <svg className="w-16 h-16 transform -rotate-90">
                <circle className="text-stone-200" strokeWidth="4" stroke="currentColor" fill="transparent" r="28" cx="32" cy="32" />
                <circle className="text-primary transition-all duration-500" strokeWidth="4" strokeDasharray={`${completionPercentage * 1.76} 176`} strokeLinecap="round" stroke="currentColor" fill="transparent" r="28" cx="32" cy="32" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                {completionPercentage === 100 ? (
                  <Trophy className="w-6 h-6 text-accent" />
                ) : (
                  <span className="text-sm font-bold text-primary">{completionPercentage}%</span>
                )}
              </div>
            </div>
            <div>
              <p className="font-semibold text-stone-900">Reading Progress</p>
              <p className="text-sm text-stone-500">{completedChapters.length} of {chapters.length} chapters complete</p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="card-cosmic p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-stone-700">Overall Progress</span>
            <span className="text-sm text-stone-500">{completedChapters.length}/{chapters.length} chapters</span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Chapter List */}
          <div className="lg:col-span-4">
            <div className="card-cosmic p-4">
              <h2 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                Chapters
              </h2>
              <ScrollArea className="h-[400px] pr-2">
                <div className="space-y-1">
                  {chapters.map((chapter) => {
                    const isCompleted = completedChapters.includes(chapter.id);
                    return (
                      <button
                        key={chapter.id}
                        onClick={() => selectChapter(chapter)}
                        className="w-full text-left px-3 py-3 rounded-xl transition-all text-sm flex items-start gap-3 text-stone-600 hover:bg-stone-100"
                        data-testid={`chapter-btn-${chapter.id}`}
                      >
                        <span className="flex-shrink-0 mt-0.5">
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5 text-secondary" />
                          ) : (
                            <Circle className="w-5 h-5 text-stone-300" />
                          )}
                        </span>
                        <span className={isCompleted ? 'line-through text-stone-400' : ''}>
                          {chapter.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Welcome Card */}
          <div className="lg:col-span-8">
            <div className="card-cosmic p-12 text-center">
              <div className="w-64 h-64 mx-auto mb-6 rounded-xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d68932fc-732f-4edc-bb74-b3bdead96d3e/artifacts/2b0qs2tn_Final%20Option.jpg"
                  alt="Illuminate Your Worth Book Cover"
                  className="w-full h-full object-cover"
                />
              </div>
              <h2 className="text-2xl font-bold text-stone-900 mb-3">Ready to Begin Your Journey?</h2>
              <p className="text-stone-600 mb-8 max-w-md mx-auto">
                Dive into "Illuminate Your Worth" and discover your path to transformation. 
                Select a chapter from the sidebar or click below to start.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => selectChapter(chapters[0])}
                  className="btn-cosmic text-lg px-8 py-4"
                  data-testid="start-reading-btn"
                >
                  Start Reading
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => window.open('https://www.amazon.com/dp/1997615223', '_blank')}
                  variant="outline"
                  className="text-lg px-8 py-4 border-2 border-accent text-accent hover:bg-accent/10"
                  data-testid="buy-hardcopy-btn"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Buy Hardcover or Paperback
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reading view
  return (
    <div 
      className={`${isFullscreen ? 'fixed inset-0 z-50 bg-stone-100 flex flex-col' : 'space-y-4'}`} 
      data-testid="ebook-page"
    >
      {/* Header */}
      <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${isFullscreen ? 'p-4 bg-white shadow-sm' : ''}`}>
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setSelectedChapter(null)}
            data-testid="back-to-chapters-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Chapters
          </Button>
          <div>
            <h1 className={`font-bold text-stone-900 ${isFullscreen ? 'text-lg' : 'text-xl'}`}>
              {selectedChapter.title}
            </h1>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Mark Complete */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="chapter-complete"
              checked={completedChapters.includes(selectedChapter.id)}
              onCheckedChange={() => toggleChapterComplete(selectedChapter.id)}
              className="data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
            />
            <label htmlFor="chapter-complete" className="text-sm text-stone-600 cursor-pointer">
              Mark complete
            </label>
          </div>

          {/* Fullscreen */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setIsFullscreen(!isFullscreen)}
            data-testid="fullscreen-btn"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className={`${isFullscreen ? 'flex-1 overflow-hidden' : ''}`}>
        <div className={`card-cosmic overflow-hidden ${isFullscreen ? 'h-full flex flex-col' : ''}`}>
          {/* PDF Controls */}
          <div className="flex items-center justify-between p-3 bg-stone-100 border-b">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || pdfLoading}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-stone-600 min-w-[100px] text-center" data-testid="page-indicator">
                Page {currentPage} of {numPages || '...'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(numPages || p, p + 1))}
                disabled={currentPage >= (numPages || 1) || pdfLoading}
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                disabled={scale <= 0.5}
                data-testid="zoom-out-btn"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-sm text-stone-600 min-w-[50px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale(s => Math.min(2.0, s + 0.1))}
                disabled={scale >= 2.0}
                data-testid="zoom-in-btn"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* PDF Document */}
          <div className={`overflow-auto bg-stone-200 flex justify-center p-4 ${isFullscreen ? 'flex-1' : 'h-[550px]'}`}>
            {pdfError ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <p className="text-red-600 mb-4">{pdfError}</p>
                <Button 
                  onClick={() => {
                    setPdfError(null);
                    setPdfLoading(true);
                  }}
                  variant="outline"
                >
                  Try Again
                </Button>
              </div>
            ) : (
              <>
                {pdfLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-stone-200/80 z-10">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <p className="text-stone-600 text-sm">Loading chapter...</p>
                    </div>
                  </div>
                )}
                <Document
                  file={getPdfUrl(selectedChapter.id)}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={null}
                  className="flex flex-col items-center"
                >
                  <Page 
                    key={`${selectedChapter.id}-page-${currentPage}`}
                    pageNumber={currentPage} 
                    scale={scale}
                    className="shadow-lg"
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      {!isFullscreen && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goToPreviousChapter}
            disabled={currentIndex === 0}
            data-testid="prev-chapter-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous Chapter
          </Button>
          
          <span className="text-sm text-stone-500">
            Chapter {currentIndex + 1} of {chapters.length}
          </span>
          
          <Button
            onClick={() => {
              if (!completedChapters.includes(selectedChapter.id)) {
                toggleChapterComplete(selectedChapter.id);
              }
              goToNextChapter();
            }}
            disabled={currentIndex === chapters.length - 1}
            className="btn-cosmic"
            data-testid="next-chapter-btn"
          >
            Next Chapter
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default EbookPage;
